import { ApolloError } from '@apollo/client';
import _ from 'lodash';
import moment from 'moment';

import type { SolidBadgeTone } from '../../../../../utils/components/SolidBadge';

import {
  GetPushBroadcastOptionsQuery,
  PushBroadcastDataFragment,
  PushBroadcastDestinationType,
  PushBroadcastFiltersInput,
  PushBroadcastInput,
  PushBroadcastStatus,
  UserSpendTier,
} from '../../../../../__gqltypes__';

export const PUSH_BROADCAST_BASE_URL = '/sp3/liveops/push-notifications';
// The route id of an editor that has not been saved yet.
export const NEW_PUSH_BROADCAST_ID = 'new';

export type PushBroadcastData = PushBroadcastDataFragment;
export type PushBroadcastOptions = GetPushBroadcastOptionsQuery;
export type PushBroadcastLanguageOption = PushBroadcastOptions['pushBroadcastLanguages'][0];
export type PushBroadcastLimitsData = PushBroadcastOptions['pushBroadcastLimits'];
export type PushBroadcastDestinationOptionData = PushBroadcastOptions['pushBroadcastDestinations'][0];

// ---------------------------------------------------------------------------------------------------------------------
// Display
// ---------------------------------------------------------------------------------------------------------------------

export const PUSH_BROADCAST_STATUS_TONE: Record<PushBroadcastStatus, SolidBadgeTone> = {
  [PushBroadcastStatus.DRAFT]: 'grey',
  [PushBroadcastStatus.SCHEDULED]: 'teal',
  [PushBroadcastStatus.PREPARING]: 'amber',
  [PushBroadcastStatus.SENDING]: 'blue',
  [PushBroadcastStatus.COMPLETED]: 'green',
  [PushBroadcastStatus.CANCELLED]: 'dark',
  [PushBroadcastStatus.FAILED]: 'red',
};

// The statuses whose batches are still being sent: the page polls while in one.
export const PUSH_BROADCAST_ACTIVE_STATUSES: ReadonlyArray<PushBroadcastStatus> = [
  PushBroadcastStatus.PREPARING,
  PushBroadcastStatus.SENDING,
];

export const PUSH_BROADCAST_FINISHED_STATUSES: ReadonlyArray<PushBroadcastStatus> = [
  PushBroadcastStatus.COMPLETED,
  PushBroadcastStatus.CANCELLED,
  PushBroadcastStatus.FAILED,
];

export function formatPushUtc(value: number | string | null | undefined): string {
  if (value == null) {
    return '—';
  }
  return moment.utc(value).format('YYYY-MM-DD HH:mm [UTC]');
}

export type PushPerson = { id: string; displayName: string; emailCredential?: string | null };

/** "Alice (alice@…)", the id while the name is loading or when the account is unknown. */
export function pushPersonLabel(id: string | null | undefined, people: ReadonlyMap<string, PushPerson>): string {
  if (!id) {
    return '—';
  }
  const person = people.get(id);
  if (!person) {
    return id;
  }
  return person.emailCredential ? `${person.displayName} (${person.emailCredential})` : person.displayName;
}

// ---------------------------------------------------------------------------------------------------------------------
// Server errors: rendered as the server reports them, all at once (the client re-implements no validation)
// ---------------------------------------------------------------------------------------------------------------------

export type PushIssue = { row?: number | null; language?: string | null; field?: string | null; message: string };

// What the operator can do about an error code, shown under the server's own message.
const PUSH_BROADCAST_ERROR_HINTS: Record<string, string> = {
  PUSH_BROADCAST_VERSION_CONFLICT:
    'Someone else saved this broadcast since you opened it. Reload the page to see their version (your unsaved edits are lost).',
  PUSH_BROADCAST_LOCKED: 'This broadcast is no longer a draft, so it cannot be changed.',
  PUSH_BROADCAST_AUDIENCE_STALE: 'The audience changed since the last count: press Count audience again.',
  PUSH_BROADCAST_BETA_REFUSED: 'Beta shares the production players: send broadcasts from the PROD admin.',
  PUSH_BROADCAST_DESTINATION_GONE: 'The destination is not current any more: pick another one.',
  PUSH_BROADCAST_NOT_FOUND: 'This broadcast does not exist (it may have been deleted).',
};

type ValidationErrorLike = { row?: unknown; language?: unknown; field?: unknown; message?: unknown };

function toIssue(raw: ValidationErrorLike): PushIssue {
  return {
    row: typeof raw.row === 'number' ? raw.row : null,
    language: typeof raw.language === 'string' ? raw.language : null,
    field: typeof raw.field === 'string' ? raw.field : null,
    message: typeof raw.message === 'string' ? raw.message : String(raw.message),
  };
}

function withHint(message: string): PushIssue[] {
  const clean = message.replace(/^GraphQL error:\s*/, '');
  const code = clean.match(/PUSH_BROADCAST_[A-Z_]+/)?.[0];
  const hint = code ? PUSH_BROADCAST_ERROR_HINTS[code] : undefined;
  return hint ? [{ message: clean }, { message: hint }] : [{ message: clean }];
}

/**
 * Every issue of a failed call. PUSH_BROADCAST_INVALID carries the full list in extensions.validationErrors (each
 * with its language and field); anything else is its message, plus what to do about it when the code is known.
 */
export function pushBroadcastErrorIssues(error: unknown): PushIssue[] {
  if (error instanceof ApolloError) {
    const issues: PushIssue[] = [];
    error.graphQLErrors.forEach((graphQLError) => {
      const list = graphQLError.extensions?.validationErrors;
      if (Array.isArray(list) && list.length > 0) {
        issues.push(...list.map((item) => toIssue(item as ValidationErrorLike)));
      } else {
        issues.push(...withHint(graphQLError.message));
      }
    });
    // A request the server refused before running it (e.g. a non-integer Int): its errors are in the result.
    const result = (error.networkError as { result?: { errors?: Array<{ message: string }> } } | null)?.result;
    (result?.errors ?? []).forEach(({ message }) => issues.push({ message }));
    if (issues.length === 0 && error.networkError) {
      issues.push({ message: `Network error: ${error.networkError.message}` });
    }
    return issues.length > 0 ? issues : withHint(error.message);
  }
  return withHint(error instanceof Error ? error.message : String(error));
}

/** "fr title: …" stays as the server wrote it; the language and field are shown only when the message lacks them. */
export function pushIssueLabel(issue: PushIssue): string {
  const prefix = [issue.row != null ? `Row ${issue.row}` : null, issue.language, issue.field]
    .filter(
      (part): part is string => !!part && !new RegExp(`(^|\\W)${_.escapeRegExp(part)}(\\W|$)`).test(issue.message)
    )
    .join(' · ');
  return prefix ? `${prefix}: ${issue.message}` : issue.message;
}

// ---------------------------------------------------------------------------------------------------------------------
// Character counts (a display: the limits are enforced by the server)
// ---------------------------------------------------------------------------------------------------------------------

/** Characters, not UTF-16 units: "🎵" is one, as the server counts it. */
export function pushCharacterCount(text: string | null | undefined): number {
  return Array.from(text ?? '').length;
}

export type PushLengthState = 'ok' | 'warn' | 'over';

export function pushLengthState(length: number, warnLength: number | null, maxLength: number): PushLengthState {
  if (length > maxLength) {
    return 'over';
  }
  if (warnLength != null && length > warnLength) {
    return 'warn';
  }
  return 'ok';
}

// ---------------------------------------------------------------------------------------------------------------------
// Editor draft <-> server
// ---------------------------------------------------------------------------------------------------------------------

export type PushTexts = Record<string, string>;

export type PushDestinationDraft = { type: PushBroadcastDestinationType; entityId: string | null };

// Inputs as typed: the server normalizes and validates them.
export type PushFiltersDraft = {
  daysSinceLastOnline: string;
  platform: string;
  appVersion: string;
  daysSinceInstall: string;
  minLevel: string;
  spendTiers: UserSpendTier[];
  gameLanguages: string[];
  userIds: string;
  excludedUserIds: string;
};

export type PushBroadcastDraft = {
  name: string;
  // '' = not picked yet: there is no default, the operator must choose.
  settingsCategory: string;
  destination: PushDestinationDraft | null;
  titleTexts: PushTexts;
  bodyTexts: PushTexts;
  filters: PushFiltersDraft;
};

export function sourceLanguageOf(languages: ReadonlyArray<PushBroadcastLanguageOption>): string {
  return (languages.find(({ isSource }) => isSource) ?? languages[0])?.code ?? '';
}

function textsFromJson(value: unknown): PushTexts {
  if (!_.isPlainObject(value)) {
    return {};
  }
  return _.pickBy(value as Record<string, unknown>, (text): text is string => typeof text === 'string') as PushTexts;
}

const numberText = (value: number | null | undefined): string => (value == null ? '' : String(value));

export function emptyPushFiltersDraft(defaultDaysSinceLastOnline: number): PushFiltersDraft {
  return {
    daysSinceLastOnline: String(defaultDaysSinceLastOnline),
    platform: '',
    appVersion: '',
    daysSinceInstall: '',
    minLevel: '',
    spendTiers: [],
    gameLanguages: [],
    userIds: '',
    excludedUserIds: '',
  };
}

export function emptyPushBroadcastDraft(
  sourceLanguage: string,
  defaultDaysSinceLastOnline: number
): PushBroadcastDraft {
  return {
    name: '',
    settingsCategory: '',
    destination: null,
    titleTexts: { [sourceLanguage]: '' },
    bodyTexts: { [sourceLanguage]: '' },
    filters: emptyPushFiltersDraft(defaultDaysSinceLastOnline),
  };
}

export function pushBroadcastToDraft(broadcast: PushBroadcastData): PushBroadcastDraft {
  const { filters } = broadcast;
  return {
    name: broadcast.name,
    settingsCategory: broadcast.settingsCategory,
    destination:
      broadcast.destination && broadcast.destination.type !== PushBroadcastDestinationType.NONE
        ? { type: broadcast.destination.type, entityId: broadcast.destination.entityId ?? null }
        : null,
    titleTexts: textsFromJson(broadcast.titleTexts),
    bodyTexts: textsFromJson(broadcast.bodyTexts),
    filters: {
      daysSinceLastOnline: numberText(filters.daysSinceLastOnline),
      platform: filters.platform ?? '',
      appVersion: filters.appVersion ?? '',
      daysSinceInstall: numberText(filters.daysSinceInstall),
      minLevel: numberText(filters.minLevel),
      spendTiers: filters.spendTiers ?? [],
      gameLanguages: filters.gameLanguages ?? [],
      userIds: (filters.userIds ?? []).join('\n'),
      excludedUserIds: (filters.excludedUserIds ?? []).join('\n'),
    },
  };
}

/** Ids pasted one per line, or separated by commas or spaces. */
export function parsePushIdList(text: string): string[] {
  return text
    .split(/[\s,;]+/)
    .map((id) => id.trim())
    .filter((id) => id.length > 0);
}

// '' = not set. Anything else goes to the server as typed, which refuses it with a message if it is not an integer.
const numberOrNull = (text: string): number | null => (text.trim() === '' ? null : Number(text.trim()));

const listOrNull = <T>(list: T[]): T[] | null => (list.length > 0 ? list : null);

export function pushFiltersToInput(filters: PushFiltersDraft): PushBroadcastFiltersInput {
  return {
    daysSinceLastOnline: numberOrNull(filters.daysSinceLastOnline),
    platform: filters.platform || null,
    appVersion: filters.appVersion.trim() || null,
    daysSinceInstall: numberOrNull(filters.daysSinceInstall),
    minLevel: numberOrNull(filters.minLevel),
    spendTiers: listOrNull(filters.spendTiers),
    gameLanguages: listOrNull(filters.gameLanguages),
    userIds: listOrNull(parsePushIdList(filters.userIds)),
    excludedUserIds: listOrNull(parsePushIdList(filters.excludedUserIds)),
  };
}

export function pushDraftToInput(draft: PushBroadcastDraft): PushBroadcastInput {
  return {
    name: draft.name,
    titleTexts: draft.titleTexts,
    bodyTexts: draft.bodyTexts,
    destination: draft.destination ? { type: draft.destination.type, entityId: draft.destination.entityId } : null,
    settingsCategory: draft.settingsCategory,
    filters: pushFiltersToInput(draft.filters),
  };
}

/** Unsaved edits to what the player receives (texts, destination, category) or to the name. */
export function isPushContentDirty(draft: PushBroadcastDraft, saved: PushBroadcastDraft): boolean {
  return !_.isEqual(_.omit(pushDraftToInput(draft), 'filters'), _.omit(pushDraftToInput(saved), 'filters'));
}

/** Unsaved edits to who receives it: the last count no longer describes the editor. */
export function isPushFiltersDirty(draft: PushBroadcastDraft, saved: PushBroadcastDraft): boolean {
  const normalize = (filters: PushFiltersDraft) => {
    const input = pushFiltersToInput(filters);
    return { ...input, spendTiers: input.spendTiers ? [...input.spendTiers].sort() : null };
  };
  return !_.isEqual(normalize(draft.filters), normalize(saved.filters));
}

// ---------------------------------------------------------------------------------------------------------------------
// Language overrides: English (the source) is mandatory, every other language an optional override
// ---------------------------------------------------------------------------------------------------------------------

/** The override languages of a draft, in the order of the language list (unknown codes last, so they stay visible). */
export function pushOverrideLanguages(
  draft: Pick<PushBroadcastDraft, 'titleTexts' | 'bodyTexts'>,
  sourceLanguage: string,
  languages: ReadonlyArray<PushBroadcastLanguageOption>
): string[] {
  const codes = _.uniq([...Object.keys(draft.titleTexts), ...Object.keys(draft.bodyTexts)]).filter(
    (code) => code !== sourceLanguage
  );
  const order = languages.map(({ code }) => code);
  return _.sortBy(codes, (code) => (order.includes(code) ? order.indexOf(code) : order.length));
}

export function addPushOverride(draft: PushBroadcastDraft, language: string): PushBroadcastDraft {
  return {
    ...draft,
    titleTexts: { ...draft.titleTexts, [language]: draft.titleTexts[language] ?? '' },
    bodyTexts: { ...draft.bodyTexts, [language]: draft.bodyTexts[language] ?? '' },
  };
}

/** Removing an override is explicit: that language then receives English. */
export function removePushOverride(draft: PushBroadcastDraft, language: string): PushBroadcastDraft {
  return { ...draft, titleTexts: _.omit(draft.titleTexts, language), bodyTexts: _.omit(draft.bodyTexts, language) };
}

export function setPushText(
  draft: PushBroadcastDraft,
  field: 'titleTexts' | 'bodyTexts',
  language: string,
  text: string
): PushBroadcastDraft {
  return { ...draft, [field]: { ...draft[field], [language]: text } };
}

export function pushLanguageName(code: string, languages: ReadonlyArray<PushBroadcastLanguageOption>): string {
  return languages.find((language) => language.code === code)?.name ?? code;
}

// ---------------------------------------------------------------------------------------------------------------------
// Destinations
// ---------------------------------------------------------------------------------------------------------------------

export const pushDestinationKey = (destination: { type: string; entityId?: string | null } | null): string =>
  destination ? `${destination.type}|${destination.entityId ?? ''}` : `${PushBroadcastDestinationType.NONE}|`;

/** The picker options; the saved destination is kept as an option when it is not offered any more. */
export function pushDestinationChoices(
  options: ReadonlyArray<PushBroadcastDestinationOptionData>,
  saved: PushBroadcastData['destination'] | null | undefined
): Array<PushBroadcastDestinationOptionData & { isCurrent: boolean }> {
  const choices = options.map((option) => ({ ...option, isCurrent: true }));
  if (saved && !choices.some((choice) => pushDestinationKey(choice) === pushDestinationKey(saved))) {
    choices.push({ ...saved, label: `${saved.label} (not current any more)`, isCurrent: false });
  }
  return choices;
}

export function pushDestinationFromKey(key: string): PushDestinationDraft | null {
  const [type, entityId] = key.split('|');
  if (!type || type === PushBroadcastDestinationType.NONE) {
    return null;
  }
  return { type: type as PushBroadcastDestinationType, entityId: entityId || null };
}

// ---------------------------------------------------------------------------------------------------------------------
// Rendered preview
// ---------------------------------------------------------------------------------------------------------------------

export type PushTextSegment = { text: string; isSample: boolean };

/** The text split around each sample value, so the preview marks "Alex" as a sample, never a real name. */
export function pushSampleSegments(text: string, samples: ReadonlyArray<string>): PushTextSegment[] {
  const values = _.uniq(samples.filter((value) => value.length > 0));
  if (values.length === 0 || text.length === 0) {
    return text.length > 0 ? [{ text, isSample: false }] : [];
  }
  const pattern = new RegExp(`(${values.map((value) => _.escapeRegExp(value)).join('|')})`, 'g');
  return text
    .split(pattern)
    .filter((part) => part.length > 0)
    .map((part) => ({ text: part, isSample: values.includes(part) }));
}

// ---------------------------------------------------------------------------------------------------------------------
// Audience
// ---------------------------------------------------------------------------------------------------------------------

/** Languages a player can receive in: English plus every override of the saved texts. */
export function pushTranslatedLanguages(broadcast: Pick<PushBroadcastData, 'titleTexts' | 'bodyTexts'>): string[] {
  const titles = textsFromJson(broadcast.titleTexts);
  const bodies = textsFromJson(broadcast.bodyTexts);
  return Object.keys(titles).filter((code) => code in bodies);
}

// ---------------------------------------------------------------------------------------------------------------------
// Translation import
// ---------------------------------------------------------------------------------------------------------------------

export type PushImportChange = {
  language: string;
  field: string;
  kind: string;
  before?: string | null;
  after?: string | null;
};

export type PushImportLanguageChange = {
  language: string;
  // new: the language had no override; removed: it will receive English; changed: an override edited.
  status: 'new' | 'removed' | 'changed';
  changes: PushImportChange[];
};

/** The import diff by language, in the order of the language list: which change, which are new, which go. */
export function groupPushImportChanges(
  changes: ReadonlyArray<PushImportChange>,
  saved: Pick<PushBroadcastData, 'titleTexts' | 'bodyTexts'>,
  languages: ReadonlyArray<PushBroadcastLanguageOption>
): PushImportLanguageChange[] {
  const titles = textsFromJson(saved.titleTexts);
  const bodies = textsFromJson(saved.bodyTexts);
  const order = languages.map(({ code }) => code);
  return _.sortBy(
    Object.entries(_.groupBy(changes, 'language')).map(([language, languageChanges]) => {
      const hadOverride = language in titles || language in bodies;
      const status: PushImportLanguageChange['status'] = !hadOverride
        ? 'new'
        : languageChanges.every(({ kind }) => kind === 'removed')
        ? 'removed'
        : 'changed';
      return { language, status, changes: languageChanges };
    }),
    ({ language }) => (order.includes(language) ? order.indexOf(language) : order.length)
  );
}

export function base64ToBlob(base64: string, type: string): Blob {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index++) {
    bytes[index] = binary.charCodeAt(index);
  }
  return new Blob([bytes], { type });
}

/** "summer-sale-push" from "Summer sale push!", for a download file name. */
export function pushFileSlug(name: string): string {
  return (
    name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 60) || 'push'
  );
}

// ---------------------------------------------------------------------------------------------------------------------
// Scheduling (UTC everywhere)
// ---------------------------------------------------------------------------------------------------------------------

/** The next full UTC hour at least an hour away: a sensible first value for the picker. */
export function defaultPushSendAt(now: number): number {
  return moment.utc(now).add(1, 'hour').startOf('hour').add(1, 'hour').valueOf();
}

/** "in 2 days 3 h", "in 45 min", "3 h ago": how far a send time is from now. */
export function pushRelativeTime(sendAt: number | string, now: number): string {
  const deltaMinutes = Math.round((moment.utc(sendAt).valueOf() - now) / 60000);
  const minutes = Math.abs(deltaMinutes);
  const days = Math.floor(minutes / (24 * 60));
  const hours = Math.floor((minutes % (24 * 60)) / 60);
  const rest = minutes % 60;
  let text: string;
  if (days > 0) {
    text = hours > 0 ? `${days} day${days > 1 ? 's' : ''} ${hours} h` : `${days} day${days > 1 ? 's' : ''}`;
  } else if (hours > 0) {
    text = rest > 0 ? `${hours} h ${rest} min` : `${hours} h`;
  } else {
    text = `${rest} min`;
  }
  return deltaMinutes >= 0 ? `in ${text}` : `${text} ago`;
}
